import HistoryIcon from '@mui/icons-material/History';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { MarkdownEditor } from 'common/forms/MarkdownField';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { Fragment, memo, useEffect, useState } from 'react';
import TimelineItem from './TimelineItem';

// Timeline owns its own expand state so toggling does NOT re-render the parent
// (and therefore does not re-render the MarkdownEditor below, which calls
// scrollIntoView internally on every update — that was causing the scroll jump).
const HistoryTimeline = ({
  timelineItems,
  historyLoading,
  onViewJson,
  timelineBottomRef,
  emptyMessage,
}) => {
  const [expandedRequestIds, setExpandedRequestIds] = useState([]);
  const [expandedEventIds, setExpandedEventIds] = useState([]);

  const toggleRequestExpand = (id) => {
    setExpandedRequestIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleEventExpand = (id) => {
    setExpandedEventIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  let lastDate = null;

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pt: 0.5 }}>
      {historyLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {!historyLoading && timelineItems.length === 0 && (
        <Typography variant='body2' color='text.secondary' sx={{ p: 2.5 }}>
          {emptyMessage}
        </Typography>
      )}
      {!historyLoading &&
        timelineItems.map((item, idx) => {
          const isLast = idx === timelineItems.length - 1;
          let dateLabel = null;
          let showDateDivider = false;
          if (item.timestamp) {
            try {
              dateLabel = format(new Date(item.timestamp), 'MMM dd, yyyy');
            } catch {
              /* ignore */
            }
          }
          if (dateLabel && dateLabel !== lastDate) {
            lastDate = dateLabel;
            showDateDivider = true;
          }

          return (
            // eslint-disable-next-line react/no-array-index-key
            <Fragment key={`${item.kind}-${idx}`}>
              {showDateDivider && (
                <Box
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    bgcolor: 'background.paper',
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    pt: 1.5,
                    pb: 0.75,
                  }}
                >
                  <Divider sx={{ flex: 1 }} />
                  <Typography
                    fontWeight={700}
                    color='text.primary'
                    sx={{ px: 1.5, flexShrink: 0, fontSize: '1.25rem', my: 2 }}
                  >
                    {dateLabel}
                  </Typography>
                  <Divider sx={{ flex: 1 }} />
                </Box>
              )}
              <TimelineItem
                item={item}
                idx={idx}
                isLast={isLast}
                expandedRequestIds={expandedRequestIds}
                expandedEventIds={expandedEventIds}
                toggleRequestExpand={toggleRequestExpand}
                toggleEventExpand={toggleEventExpand}
                onViewJson={onViewJson}
              />
            </Fragment>
          );
        })}
      <div ref={timelineBottomRef} />
    </Box>
  );
};

HistoryTimeline.propTypes = {
  timelineItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  historyLoading: PropTypes.bool.isRequired,
  onViewJson: PropTypes.func.isRequired,
  timelineBottomRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
  emptyMessage: PropTypes.string,
};

HistoryTimeline.defaultProps = {
  emptyMessage: 'No history available.',
};

const MemoTimeline = memo(HistoryTimeline);

const HistoryPanel = ({
  timelineItems,
  historyLoading,
  notesLoading,
  onViewJson,
  timelineBottomRef,
  newNoteText,
  setNewNoteText,
  savingNote,
  handleAddNote,
  isShippingNote,
  setIsShippingNote,
  historyEnabled,
  onHistoryFirstActivated,
}) => {
  // Notes = 0 (primary/default), History = 1
  const [activeTab, setActiveTab] = useState(0);
  // True once the first history fetch completes — prevents flashing "No history" before
  // historyLoading has a chance to become true after historyEnabled flips.
  const [historyHasLoaded, setHistoryHasLoaded] = useState(false);

  useEffect(() => {
    if (historyEnabled && !historyLoading) setHistoryHasLoaded(true);
  }, [historyEnabled, historyLoading]);

  // Show spinner when: fetch triggered but not yet done, OR effect hasn't set loading yet
  const showHistoryLoading = !historyHasLoaded || historyLoading;

  const handleTabChange = (_, val) => {
    setActiveTab(val);
    if (val === 1) onHistoryFirstActivated();
  };

  const eventItems = timelineItems.filter((item) => item.kind !== 'note');
  const noteItems = timelineItems.filter((item) => item.kind === 'note');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Tabs */}
      <Box sx={{ borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant='fullWidth'
          sx={{ minHeight: 44 }}
          TabIndicatorProps={{ sx: { height: 3 } }}
        >
          <Tab
            icon={<NoteAltIcon fontSize='small' />}
            iconPosition='start'
            label='Notes'
            sx={{ minHeight: 44, fontSize: '0.75rem', textTransform: 'none', py: 1 }}
          />
          <Tab
            icon={<HistoryIcon fontSize='small' />}
            iconPosition='start'
            label='History'
            sx={{ minHeight: 44, fontSize: '0.75rem', textTransform: 'none', py: 1 }}
          />
        </Tabs>
      </Box>

      {/* Notes tab (always mounted) */}
      <Box
        sx={{
          display: activeTab === 0 ? 'flex' : 'none',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <MemoTimeline
          timelineItems={noteItems}
          historyLoading={notesLoading}
          onViewJson={onViewJson}
          timelineBottomRef={timelineBottomRef}
          emptyMessage='No notes available.'
        />
        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', flexShrink: 0 }}>
          <MarkdownEditor
            id='order-view-dialog-note'
            height='150px'
            value={newNoteText}
            onChange={setNewNoteText}
            onBlur={() => {}}
            onFocus={() => {}}
            autoFocus={false}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 0.75,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={isShippingNote}
                  onChange={(e) => setIsShippingNote(e.target.checked)}
                  size='small'
                />
              }
              label={
                <Typography variant='body2' sx={{ fontSize: '0.75rem' }}>
                  Is Shipping Note
                </Typography>
              }
              sx={{ ml: 0 }}
            />
            <LoadingButton
              loading={savingNote}
              variant='contained'
              size='small'
              disabled={!newNoteText.trim()}
              onClick={handleAddNote}
            >
              Add Note
            </LoadingButton>
          </Box>
        </Box>
      </Box>

      {/* History tab — lazy: only rendered after first visit, then cached in DOM */}
      {historyEnabled && (
        <Box
          sx={{
            display: activeTab === 1 ? 'flex' : 'none',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          <MemoTimeline
            timelineItems={eventItems}
            historyLoading={showHistoryLoading}
            onViewJson={onViewJson}
            timelineBottomRef={timelineBottomRef}
          />
        </Box>
      )}
    </Box>
  );
};

HistoryPanel.propTypes = {
  timelineItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  historyLoading: PropTypes.bool.isRequired,
  notesLoading: PropTypes.bool.isRequired,
  onViewJson: PropTypes.func.isRequired,
  timelineBottomRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
  newNoteText: PropTypes.string.isRequired,
  setNewNoteText: PropTypes.func.isRequired,
  savingNote: PropTypes.bool.isRequired,
  handleAddNote: PropTypes.func.isRequired,
  isShippingNote: PropTypes.bool.isRequired,
  setIsShippingNote: PropTypes.func.isRequired,
  historyEnabled: PropTypes.bool.isRequired,
  onHistoryFirstActivated: PropTypes.func.isRequired,
};

export default HistoryPanel;
